import {
  associateReadingType,
  csvEscape,
  parseDateToISO
} from "helpers/variables";
import Papa from "papaparse";

const parseCSV = (rawCSV: string): Record<string, string>[] => {
  if (!rawCSV?.trim()) return [];
  const result = Papa.parse<Record<string, string>>(rawCSV, {
    header: true,
    skipEmptyLines: true,
  });
  return result.data;
};

const serializeCSV = (
  rows: Record<string, string>[],
  columnOrder: string[]
): string | null => {
  if (rows.length === 0) return null;
  const headerLine = columnOrder.join(",");
  const rowLines = rows
    .map((row) => columnOrder.map((col) => csvEscape(row[col] ?? "")).join(","))
    .join("\n");
  return `${headerLine}\n${rowLines}`;
};

const extractSeconds = (value: string | undefined): string => {
  if (!value) return "";
  const match = value.match(/(-?\d+(\.\d+)?)/);
  return match ? match[1] : "";
};

const readingConfig: Record <
  string,
  { columns: { inKey: string; outKey: string }[] }
> = {
  bp: {
    columns: [
      { inKey: "Systolic", outKey: "systolic" },
      { inKey: "Diastolic", outKey: "diastolic" },
      { inKey: "Pulse", outKey: "pulse" },
    ],
  },
  bgl: {
    columns: [
      { inKey: "Glucose", outKey: "glucose" },
      { inKey: "Units", outKey: "unit" },
    ],
  },
  spo2: {
    columns: [
      { inKey: "Spo2", outKey: "spo2" },
      { inKey: "Pulse", outKey: "pulse" },
    ],
  },
  wt: {
    columns: [
      { inKey: "Weight", outKey: "weight" },
      { inKey: "Units", outKey: "unit" },
    ],
  },
  temp: {
    columns: [
      { inKey: "Temp", outKey: "temp" },
      { inKey: "Units", outKey: "unit" },
    ],
  },
  hrt: {
    columns: [
      { inKey: "Pulse", outKey: "pulse" },
    ],
  },
  activity: {
    columns: [
      { inKey: "Steps", outKey: "steps" },
      { inKey: "Calories", outKey: "calories" },
      { inKey: "Distance", outKey: "distance" },
    ],
  },
  spir: {
    columns: [{ inKey: "Medication", outKey: "medication" }],
  },
  sleep: {
    columns: [
      { inKey: "Hours", outKey: "hours" },
    ],
  },
};

const normalizeDateForFilename = (date: string): string => {
  const match = String(date).match(/^\d{4}-\d{2}-\d{2}/);
  if (match) return match[0];
  const currentTime = new Date(date);
  if (!isNaN(currentTime.getTime())) {
    return currentTime.toISOString().slice(0, 10);
  }
  return String(date).replace(/[^\w-]/g, "");
};

export const buildMetadataHeader = (
  memberId: string,
  start: string,
  end: string,
  includesList: string[]
): string => {
  return [
    `Member ID,${csvEscape(memberId)}`,
    `Start Date,${csvEscape(start)}`,
    `End Date,${csvEscape(end)}`,
    `Exported At,${new Date().toISOString()}`,
    `Includes,${includesList.join(";")}`,
  ].join("\n");
};

export const formatEncountersSection = (rawCSV: string): string | null => {
  const rows = parseCSV(rawCSV);
  if (rows.length === 0) return null;

  const transformed = rows.map((row) => ({
    timestamp:
      row["Day"] && row["Timestamp"]
        ? parseDateToISO(`${row["Day"]} ${row["Timestamp"]}`, "encounter")
        : "",
    type: row["Type"] ?? "",
    total_duration_seconds: extractSeconds(row["Total Duration"]),
    escalated_duration_seconds: extractSeconds(row["Escalated Duration"]),
    coordinator: row["Coordinator Name"] ?? "",
    note: row["Note Content"] ?? "",
  }));

  const body = serializeCSV(transformed, [
    "timestamp",
    "type",
    "total_duration_seconds",
    "escalated_duration_seconds",
    "coordinator",
    "note",
  ]);

  return body ? `ENCOUNTERS\n${body}` : null;
};

export const formatNotesSection = (rawCSV: string): string | null => {
  const rows = parseCSV(rawCSV);
  if (rows.length === 0) return null;

  const transformed = rows.map((row) => ({
    // Note: backend Date field is currently empty. When fixed, may need a date parser added here.
    date: row["Date"] ?? "",
    author: row["Author Name"] ?? "",
    type: row["Type"] ?? "",
    content: row["Content"] ?? "",
  }));

  const body = serializeCSV(transformed, ["date", "author", "type", "content"]);
  return body ? `NOTES\n${body}` : null;
};

export const formatNotificationsSection = (rawCSV: string): string | null => {
  const rows = parseCSV(rawCSV);
  if (rows.length === 0) return null;

  const transformed = rows.map((row) => ({
    received_at: parseDateToISO(row["Received At"], "notification"),
    reading_type: row["Reading Type"] ?? "",
    breach_type: row["Breach Type"] ?? "",
    acknowledged: row["Acknowledged"] ?? "",
    cleared_at: parseDateToISO(row["Cleared At"], "notification"),
    flagged: row["Flagged"] ?? "",
  }));

  const body = serializeCSV(transformed, [
    "received_at",
    "reading_type",
    "breach_type",
    "acknowledged",
    "cleared_at",
    "flagged",
  ]);

  return body ? `NOTIFICATIONS\n${body}` : null;
};

export const formatReadingsSection = (
  vital: string,
  rawCSV: string
): string | null => {
  const rows = parseCSV(rawCSV);
  if (rows.length === 0) return null;

  const config = readingConfig[vital];
  if (!config) return null; // Unknown vital, skip silently

  const transformed = rows.map((row) => {
    const out: Record<string, string> = {
      measured_at: parseDateToISO(row["Time Stamp"], "reading"),
    };
    config.columns.forEach((col: any) => {
      out[col.outKey] = row[col.inKey] ?? "";
    });
    return out;
  });

  const columnOrder = ["measured_at", ...config.columns.map((c: any) => c.outKey)];
  const body = serializeCSV(transformed, columnOrder);
  console.log(`[${vital}] body:`, body ? body.slice(0, 200) : 'NULL');
  if (!body) return null;

  const displayName = associateReadingType(vital).toUpperCase();
  return `READINGS - ${displayName}\n${body}`;
};

export const combineMemberExport = (
  metadataHeader: string,
  sections: (string | null)[]
): string => {
  const validSections = sections.filter((s): s is string => Boolean(s));
  if (validSections.length === 0) {
    return metadataHeader; // Header only, no data found
  }
  return [metadataHeader, ...validSections].join("\n\n");
};

export const buildExportFilename = (
  memberId: string,
  start: string,
  end: string,
  dataType?: string,
  extension: "csv" | "pdf" = "csv"
): string => {
  const startDay = normalizeDateForFilename(start);
  const endDay = normalizeDateForFilename(end);
  const parts = dataType
    ? [memberId, dataType, startDay, "to", endDay]
    : [memberId, startDay, "to", endDay];
  return `${parts.join("_")}.${extension}`;
};